import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Avatar,
  Button,
  IconButton,
  TextField,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Divider,
  Chip,
  Alert,
  CircularProgress,
  Breadcrumbs,
  Link,
  InputAdornment,
  Fab,
} from '@mui/material';
import {
  ArrowBack,
  Send,
  AttachFile,
  Schedule,
  Person,
  Reply,
  Forward,
  MoreVert,
  Phone,
  VideoCall,
} from '@mui/icons-material';

interface Message {
  id: number;
  senderId: number;
  senderName: string;
  senderType: 'parent' | 'teacher';
  content: string;
  timestamp: Date;
  isRead: boolean;
  attachments?: {
    name: string;
    type: string;
    url: string;
  }[];
}

interface MessageThread {
  id: number;
  teacherId: number;
  teacherName: string;
  teacherEmail: string;
  subject: string;
  childName: string;
  isUrgent: boolean;
  messages: Message[];
}

const ParentMessageThread: React.FC = () => {
  const navigate = useNavigate();
  const { threadId } = useParams<{ threadId: string }>();
  const [loading, setLoading] = useState(true);
  const [thread, setThread] = useState<MessageThread | null>(null);
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!threadId) {
      navigate('/parent-messages');
      return;
    }

    // Mock data - replace with API call
    setLoading(true);
    setTimeout(() => {
      const mockThread: MessageThread = {
        id: parseInt(threadId),
        teacherId: 1,
        teacherName: 'Ms. Sarah Wilson',
        teacherEmail: 'sarah.wilson@school.edu',
        subject: 'Emma\'s Math Progress',
        childName: 'Emma Johnson',
        isUrgent: false,
        messages: [
          {
            id: 1,
            senderId: 1,
            senderName: 'Ms. Sarah Wilson',
            senderType: 'teacher',
            content: 'Hello! I wanted to reach out regarding Emma\'s recent progress in mathematics. She has been doing exceptionally well with algebra concepts.',
            timestamp: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000), // 3 days ago
            isRead: true
          },
          {
            id: 2,
            senderId: 100, // Parent ID
            senderName: 'Sarah Johnson',
            senderType: 'parent',
            content: 'Thank you for reaching out! We\'ve noticed Emma spending more time on her math homework lately. Is there anything specific we should be helping her with at home?',
            timestamp: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000), // 2 days ago
            isRead: true
          },
          {
            id: 3,
            senderId: 1,
            senderName: 'Ms. Sarah Wilson',
            senderType: 'teacher',
            content: 'That\'s wonderful to hear! Emma\'s dedication is really showing. I think she\'s ready for more advanced problems. I\'d like to discuss the possibility of advanced placement for next semester. Would you be available for a quick meeting this week?',
            timestamp: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000), // 1 day ago
            isRead: true
          },
          {
            id: 4,
            senderId: 1,
            senderName: 'Ms. Sarah Wilson',
            senderType: 'teacher',
            content: 'Emma has shown great improvement in algebra. I\'d like to discuss her advanced placement options.',
            timestamp: new Date(Date.now() - 2 * 60 * 60 * 1000), // 2 hours ago
            isRead: false
          }
        ]
      };

      setThread(mockThread);
      setLoading(false);
    }, 1000);
  }, [threadId, navigate]);

  const handleSendMessage = async () => {
    if (!newMessage.trim() || !thread) return;

    setSending(true);
    
    // Mock sending - replace with API call
    setTimeout(() => {
      const newMsg: Message = {
        id: thread.messages.length + 1,
        senderId: 100, // Parent ID
        senderName: 'Sarah Johnson',
        senderType: 'parent',
        content: newMessage.trim(),
        timestamp: new Date(),
        isRead: false
      };

      setThread(prev => prev ? {
        ...prev,
        messages: [...prev.messages, newMsg]
      } : null);

      setNewMessage('');
      setSending(false);
    }, 1000);
  };

  const formatMessageTime = (date: Date) => {
    const now = new Date();
    const diffInHours = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60));
    
    if (diffInHours < 1) return 'Just now';
    if (diffInHours < 24) return `${diffInHours}h ago`;
    
    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 7) return `${diffInDays}d ago`;
    
    return date.toLocaleDateString() + ' at ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress size={60} />
      </Box>
    );
  }

  if (!thread) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="error">
          Message thread not found. 
          <Button onClick={() => navigate('/parent-messages')} sx={{ ml: 2 }}>
            Back to Messages
          </Button>
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      {/* Breadcrumb */}
      <Box sx={{ mb: 3 }}>
        <Breadcrumbs aria-label="breadcrumb">
          <Link
            color="inherit"
            href="#"
            onClick={() => navigate('/parent-dashboard')}
            sx={{ cursor: 'pointer' }}
          >
            Parent Dashboard
          </Link>
          <Link
            color="inherit"
            href="#"
            onClick={() => navigate('/parent-messages')}
            sx={{ cursor: 'pointer' }}
          >
            Messages
          </Link>
          <Typography color="text.primary">
            {thread.teacherName}
          </Typography>
        </Breadcrumbs>
      </Box>

      {/* Header */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <IconButton onClick={() => navigate('/parent-messages')} sx={{ mr: 2 }}>
              <ArrowBack />
            </IconButton>
            <Avatar sx={{ bgcolor: 'primary.main', mr: 2, width: 48, height: 48 }}>
              {thread.teacherName.charAt(0)}
            </Avatar>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {thread.teacherName}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {thread.teacherEmail}
              </Typography>
              <Typography variant="caption" color="primary">
                Re: {thread.subject} • {thread.childName}
              </Typography>
            </Box>
          </Box>
          
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button variant="outlined" startIcon={<Schedule />}>
              Book Meeting
            </Button>
            <Button variant="outlined" startIcon={<Phone />}>
              Call
            </Button>
            <IconButton>
              <MoreVert />
            </IconButton>
          </Box>
        </Box>

        {thread.isUrgent && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            This conversation is marked as urgent and requires immediate attention.
          </Alert>
        )}
      </Paper>

      {/* Messages */}
      <Paper sx={{ mb: 3, maxHeight: '60vh', overflow: 'auto' }}>
        <List sx={{ p: 0 }}>
          {thread.messages.map((message, index) => (
            <React.Fragment key={message.id}>
              <ListItem
                alignItems="flex-start"
                sx={{
                  flexDirection: message.senderType === 'parent' ? 'row-reverse' : 'row',
                  px: 3,
                  py: 2
                }}
              >
                <ListItemAvatar sx={{ 
                  minWidth: message.senderType === 'parent' ? 0 : 56,
                  mr: message.senderType === 'parent' ? 0 : 2,
                  ml: message.senderType === 'parent' ? 2 : 0
                }}>
                  <Avatar 
                    sx={{ 
                      bgcolor: message.senderType === 'parent' ? 'secondary.main' : 'primary.main',
                      width: 40,
                      height: 40
                    }}
                  >
                    {message.senderName.charAt(0)}
                  </Avatar>
                </ListItemAvatar>
                
                <Box sx={{ 
                  flex: 1,
                  textAlign: message.senderType === 'parent' ? 'right' : 'left'
                }}>
                  <Card 
                    sx={{ 
                      maxWidth: '80%',
                      ml: message.senderType === 'parent' ? 'auto' : 0,
                      mr: message.senderType === 'parent' ? 0 : 'auto',
                      bgcolor: message.senderType === 'parent' ? 'primary.main' : 'grey.100',
                      color: message.senderType === 'parent' ? 'white' : 'text.primary'
                    }}
                  >
                    <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                        <Typography 
                          variant="caption" 
                          sx={{ 
                            fontWeight: 600,
                            color: message.senderType === 'parent' ? 'rgba(255,255,255,0.8)' : 'text.secondary'
                          }}
                        >
                          {message.senderName}
                        </Typography>
                        <Typography 
                          variant="caption" 
                          sx={{ 
                            color: message.senderType === 'parent' ? 'rgba(255,255,255,0.6)' : 'text.secondary'
                          }}
                        >
                          {formatMessageTime(message.timestamp)}
                        </Typography>
                      </Box>
                      <Typography variant="body2">
                        {message.content}
                      </Typography>
                      {!message.isRead && message.senderType === 'teacher' && (
                        <Chip 
                          label="New" 
                          size="small" 
                          color="error" 
                          sx={{ mt: 1, height: 20, fontSize: '0.7rem' }}
                        />
                      )}
                    </CardContent>
                  </Card>
                </Box>
              </ListItem>
              {index < thread.messages.length - 1 && (
                <Divider variant="middle" sx={{ my: 1 }} />
              )}
            </React.Fragment>
          ))}
        </List>
      </Paper>

      {/* Message Input */}
      <Paper sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-end' }}>
          <TextField
            fullWidth
            multiline
            maxRows={4}
            placeholder="Type your message..."
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleSendMessage();
              }
            }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton>
                    <AttachFile />
                  </IconButton>
                </InputAdornment>
              )
            }}
          />
          <Button
            variant="contained"
            endIcon={<Send />}
            onClick={handleSendMessage}
            disabled={!newMessage.trim() || sending}
            sx={{ minWidth: 100 }}
          >
            {sending ? <CircularProgress size={20} /> : 'Send'}
          </Button>
        </Box>
        
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
          Press Enter to send, Shift+Enter for new line
        </Typography>
      </Paper>
    </Container>
  );
};

export default ParentMessageThread;

